import React, { useState, useEffect, useCallback } from "react";
import {
  Key, Loader, RefreshCw, AlertTriangle, Trash2,
  Plus, Eye, EyeOff, Github, Globe, Settings, Cloud, Building2, Shield, FileText,
  CheckCircle, Save
} from "lucide-react";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useHasLicense } from "../contexts/LicenseContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import SurfaceScanEnrollDealModal from "./SurfaceScanEnrollDealModal";
import SurfaceScanEnrollBusinessUnitModal from "./SurfaceScanEnrollBusinessUnitModal";
import SurfaceScanEnrollVendorModal from "./SurfaceScanEnrollVendorModal";
import SecretsScanEnrollEntityModal from "./SecretsScanEnrollEntityModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import UnavailableNote from "./common/UnavailableNote";
import { useModuleAvailability } from "../hooks/useModuleAvailability";

// `module`: the egress-catalog module a tab needs; the tab is hidden when it is unavailable.
const TABS = [
  { id: "secrets", label: "Secrets Scan", Icon: Key,   color: "purple", module: "secretsScanning" },
  { id: "surface", label: "Surface Scan", Icon: Globe, color: "purple", module: "surfaceScan" },
];

export const TAB_STYLE = {
  purple: {
    active:   "border-purple-600 text-purple-700 bg-purple-50",
    inactive: "border-transparent text-content-secondary hover:text-content-primary hover:border-gray-300",
    btn:      "bg-purple-700 hover:bg-purple-600 focus:ring-purple-500",
    spin:     "text-purple-700",
    check:    "text-purple-600",
    input:    "focus:border-purple-500 focus:ring-purple-500",
  },
  orange: {
    active:   "border-orange-500 text-orange-600 bg-orange-50",
    inactive: "border-transparent text-content-secondary hover:text-content-primary hover:border-gray-300",
    btn:      "bg-orange-600 hover:bg-orange-500 focus:ring-orange-500",
    spin:     "text-orange-600",
    check:    "text-orange-600",
    input:    "focus:border-orange-500 focus:ring-orange-500",
  },
  blue: {
    active:   "border-blue-600 text-blue-700 bg-blue-50",
    inactive: "border-transparent text-content-secondary hover:text-content-primary hover:border-gray-300",
    btn:      "bg-blue-700 hover:bg-blue-600 focus:ring-blue-500",
    spin:     "text-blue-700",
    check:    "text-blue-600",
    input:    "focus:border-blue-500 focus:ring-blue-500",
  },
};

export function ConnectorsConfigContent() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const { isModuleAvailable } = useModuleAvailability();
  const tabs = TABS.filter(t => isModuleAvailable(t.module));
  const initialTab = ["secrets", "surface"].includes(tabParam) ? tabParam : "surface";
  const [activeTab, setActiveTab] = useState(initialTab);
  // The selected tab may be unavailable (?tab= link, or flags loaded after mount): fall back to
  // the first available tab, or to none when neither module is available.
  const currentTab = tabs.find(t => t.id === activeTab) ?? tabs[0] ?? null;
  const s = currentTab ? TAB_STYLE[currentTab.color] : null;

  // Track org changes so tab panels remount when the user switches organizations.
  // localStorage is always up-to-date because the org switcher writes it before navigation.
  const [orgKey, setOrgKey] = useState(() => {
    const o = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
    return o?.organizationId ?? "none";
  });
  useEffect(() => {
    const sync = () => {
      const o = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
      setOrgKey(o?.organizationId ?? "none");
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

      {/* Page header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="p-2 bg-theme-secondary rounded-lg border border-border">
          <Settings className="h-5 w-5 text-content-secondary" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-content-primary">Connector Configuration</h1>
          <p className="text-sm text-content-secondary">Configure integrations for Secrets Scan and Surface Scan</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-border mb-8">
        {tabs.map(({ id, label, Icon, color: tc }) => {
          const ts = TAB_STYLE[tc];
          const isActive = currentTab?.id === id;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all -mb-px ${isActive ? ts.active : ts.inactive}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Tab panels — keyed by orgKey so they remount when the org changes */}
      {currentTab?.id === "secrets" && <SecretsTab key={`secrets-${orgKey}`} s={s} />}
      {currentTab?.id === "surface" && <SurfaceTab key={`surface-${orgKey}`} s={s} />}
      {!currentTab && <UnavailableNote className="text-sm" />}

    </div>
  );
}

export default function ConnectorsConfig() {
  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <ConnectorsConfigContent />
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ErrorBanner({ msg }) {
  return msg ? (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <p className="text-sm text-red-700 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />{msg}
      </p>
    </div>
  ) : null;
}

function SuccessBanner({ msg }) {
  return msg ? (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
      <p className="text-sm text-green-700">{msg}</p>
    </div>
  ) : null;
}

function Card({ children, className = "" }) {
  return (
    <div className={`bg-theme-secondary rounded-lg shadow-theme border border-border p-6 mb-6 ${className}`}>
      {children}
    </div>
  );
}

function SectionTitle({ icon: Icon, label, colorClass, sub }) {
  return (
    <div className="mb-4">
      <h2 className="text-base font-semibold text-content-primary flex items-center gap-2">
        <Icon className={`h-5 w-5 ${colorClass}`} />
        {label}
      </h2>
      {sub && <p className="text-sm text-content-secondary mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Secrets Scan Tab ─────────────────────────────────────────────────────────

export function SecretsTab({ s }) {
  const apiCall = useApi();
  const navigate = useNavigate();
  const { canManageOrganizationRisks, canManageDeal, canManageBusinessUnit, canCreateVendor, canManageAnyDeal, canManageAnyBusinessUnit } = useRBAC();
  const hasLicense = useHasLicense();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  // Per-scope gates: org-level config (tokens, org targets, keywords, settings) needs
  // Enterprise Risk Manager; deal/BU/vendor targets need management rights on that scope.
  const orgId = org?.organizationId;
  const canManageOrg = canManageOrganizationRisks(orgId);
  const canAddDeal = canManageAnyDeal(orgId);
  const canAddBU = canManageAnyBusinessUnit(orgId);
  const canAddVendor = canCreateVendor(orgId);
  const showDeals = hasLicense("Deals");
  const showBusinessUnits = hasLicense("BusinessUnits");
  const showTPRM = hasLicense("TPRM");

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [addingKw, setAddingKw]    = useState(false);
  const [deletingKw, setDeletingKw] = useState(null);
  const [frequency, setFrequency]  = useState(24);
  const [isEnabled, setIsEnabled]  = useState(true);

  // Unified scan targets
  const [scanTargets, setScanTargets] = useState([]);
  const [deleteTargetIdx, setDeleteTargetIdx] = useState(null); // index into scanTargets pending confirm
  const [deletingTarget, setDeletingTarget] = useState(false);

  // Org-level source-code connections — every target scans with a connection's token
  const [connections, setConnections] = useState([]);

  // Inline org-level add target (a connection id as string)
  const [addingOrgTarget, setAddingOrgTarget] = useState(false);
  const [newOrgSource, setNewOrgSource] = useState("");

  // Entity enrollment modals
  const [enrollModal, setEnrollModal] = useState(null); // "deal" | "bu" | "vendor" | null

  // Entity name lookups
  const [dealNames, setDealNames] = useState({});
  const [buNames, setBuNames]     = useState({});
  const [vendorNames, setVendorNames] = useState({});

  const applyConfig = (data) => {
    if (!data) {
      setKeywords([]); setFrequency(24); setIsEnabled(true);
      setScanTargets([]);
      return;
    }
    setKeywords(data.domainKeywords || []);
    setFrequency(data.scanFrequencyHours || 24);
    setIsEnabled(data.isEnabled ?? true);
    setScanTargets(data.scanTargets || []);
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const fetches = [apiCall("GET", "/secrets-scan/config").catch(() => null)];
      if (showDeals) fetches.push(apiCall("GET", "/deals/all").catch(() => []));
      if (showBusinessUnits) fetches.push(apiCall("GET", "/businessUnits/all").catch(() => []));
      if (showTPRM) fetches.push(apiCall("GET", "/TPRMVendor/vendors?pageSize=100").catch(() => null));

      const results = await Promise.all(fetches);
      const data = results[0];
      let idx = 1;
      if (showDeals) {
        const deals = results[idx++] || [];
        const map = {}; deals.forEach(d => { map[d.dealID] = d.dealName; }); setDealNames(map);
      }
      if (showBusinessUnits) {
        const bus = results[idx++] || [];
        const map = {}; bus.forEach(b => { map[b.businessUnitId] = b.businessUnitName; }); setBuNames(map);
      }
      if (showTPRM) {
        const vendorRes = results[idx++];
        const vendors = vendorRes?.items || vendorRes || [];
        const map = {}; vendors.forEach(v => { map[v.vendorId] = v.vendorName; }); setVendorNames(map);
      }
      applyConfig(data);

      const conns = await apiCall("GET", "/source-code-connections").catch(() => []);
      setConnections(conns || []);
      setNewOrgSource((conns || []).length > 0 ? String(conns[0].id) : "");
    } catch { setError("Failed to load configuration."); }
    finally { setLoading(false); }
  };

  /// A target entry auto-links to a connection when provider+target match (case-insensitive).
  const findConnection = (provider, target) =>
    connections.find(c => c.provider.toLowerCase() === provider.toLowerCase()
      && c.target.toLowerCase() === target.toLowerCase());

  useEffect(() => {
    load();
    const handleStorage = (e) => { if (e.key === "selectedOrganization") load(); };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const basePayload = (kw, targets) => ({
    scanTargets: targets ?? scanTargets,
    domainKeywords: kw ?? keywords,
    isEnabled,
    scanFrequencyHours: Number(frequency) || 24,
  });

  const doSave = async (extra = {}) => {
    const saved = await apiCall("POST", "/secrets-scan/config", { ...basePayload(), ...extra });
    applyConfig(saved);
    return saved;
  };

  // Org-level target management — targets are always created from a connection
  // (provider/target/baseUrl/token all come from it).
  const handleAddOrgTarget = async () => {
    const conn = connections.find(c => String(c.id) === newOrgSource);
    if (!conn) return;
    const provider = conn.provider.toLowerCase();
    if (scanTargets.some(e => e.provider === provider && e.target.toLowerCase() === conn.target.toLowerCase() && !e.dealId && !e.businessUnitId && !e.vendorId)) {
      setError("Target already added."); return;
    }
    const entry = { provider, target: conn.target, baseUrl: conn.baseUrl || null, dealId: null, businessUnitId: null, vendorId: null, connectionId: conn.id };
    setAddingOrgTarget(true); setError(null);
    try { await doSave({ scanTargets: [...scanTargets, entry] }); }
    catch (err) { setError(err?.response?.data?.message || "Failed to add target."); }
    finally { setAddingOrgTarget(false); }
  };

  // Single-step "Are you sure?" confirm (no typed Confirm) — a removed target can be re-added
  // from its connection at any time.
  const handleDeleteTarget = (idx) => setDeleteTargetIdx(idx);

  const confirmDeleteTarget = async () => {
    const idx = deleteTargetIdx;
    if (idx == null) return;
    setDeletingTarget(true); setError(null);
    try { await doSave({ scanTargets: scanTargets.filter((_, i) => i !== idx) }); setDeleteTargetIdx(null); }
    catch (err) { setError(err?.response?.data?.message || "Failed to remove target."); setDeleteTargetIdx(null); }
    finally { setDeletingTarget(false); }
  };

  // Entity enrollment callback — the scoped target must resolve to a connection (the backend
  // rejects unlinked targets); guard client-side for a friendlier message.
  const handleEntityEnrolled = async (entityId, provider, target, baseUrl) => {
    setError(null);
    const entityType = enrollModal;
    const conn = findConnection(provider, target);
    if (!conn) {
      setError(`No ${provider === "github" ? "GitHub" : "GitLab"} connection found for "${target}". Add one under Settings → Integrations → Source Code Connections first.`);
      return;
    }
    const entry = { provider, target, baseUrl: baseUrl || conn.baseUrl || null, dealId: null, businessUnitId: null, vendorId: null, connectionId: conn.id };
    if (entityType === "deal") entry.dealId = entityId;
    else if (entityType === "bu") entry.businessUnitId = entityId;
    else if (entityType === "vendor") entry.vendorId = entityId;
    try { await doSave({ scanTargets: [...scanTargets, entry] }); }
    catch (err) { setError(err?.response?.data?.message || `Failed to add ${entityType} target.`); }
  };

  // Domain keyword handlers
  const handleAddKw = async () => {
    const kw = newKeyword.trim();
    if (!kw) { setError("Please enter a keyword."); return; }
    if (keywords.includes(kw)) { setError("Already added."); return; }
    setAddingKw(true); setError(null);
    try { await doSave({ domainKeywords: [...keywords, kw] }); setNewKeyword(""); }
    catch { setError("Failed to add keyword."); }
    finally { setAddingKw(false); }
  };

  const handleDeleteKw = async (kw) => {
    if (!window.confirm(`Remove "${kw}"?`)) return;
    setDeletingKw(kw); setError(null);
    try { await doSave({ domainKeywords: keywords.filter(k => k !== kw) }); }
    catch (err) { setError(err?.response?.data?.message || "Failed to remove keyword."); }
    finally { setDeletingKw(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader className={`h-7 w-7 animate-spin ${s.spin}`} /></div>;

  // Group targets by scope
  const orgTargets = scanTargets.filter(t => !t.dealId && !t.businessUnitId && !t.vendorId);
  const dealGroups = {};
  const buGroups = {};
  const vendorGroups = {};
  scanTargets.forEach((entry, idx) => {
    if (entry.dealId) {
      if (!dealGroups[entry.dealId]) dealGroups[entry.dealId] = { dealId: entry.dealId, targets: [] };
      dealGroups[entry.dealId].targets.push({ ...entry, _idx: idx });
    }
    if (entry.businessUnitId) {
      if (!buGroups[entry.businessUnitId]) buGroups[entry.businessUnitId] = { businessUnitId: entry.businessUnitId, targets: [] };
      buGroups[entry.businessUnitId].targets.push({ ...entry, _idx: idx });
    }
    if (entry.vendorId) {
      if (!vendorGroups[entry.vendorId]) vendorGroups[entry.vendorId] = { vendorId: entry.vendorId, targets: [] };
      vendorGroups[entry.vendorId].targets.push({ ...entry, _idx: idx });
    }
  });
  const dealGroupList = Object.values(dealGroups);
  const buGroupList = Object.values(buGroups);
  const vendorGroupList = Object.values(vendorGroups);

  const ProviderBadge = ({ provider }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
      provider === "github" ? "bg-gray-900 text-white"
        : provider === "azuredevops" ? "bg-blue-600 text-white" : "bg-orange-500 text-white"}`}>
      {provider === "github" ? "GitHub" : provider === "azuredevops" ? "Azure DevOps" : "GitLab"}
    </span>
  );

  const TargetRow = ({ entry, onDelete, canDelete }) => (
    <div className="flex items-center justify-between py-2 px-3 bg-theme-primary rounded-md border border-border">
      <div className="flex items-center gap-2">
        <ProviderBadge provider={entry.provider} />
        <span className="text-sm text-content-primary font-mono">{entry.target}</span>
        {entry.baseUrl && <span className="text-xs text-content-secondary">({entry.baseUrl})</span>}
      </div>
      {canDelete && onDelete && (
        <button onClick={onDelete} className="text-red-600 hover:text-red-800 p-1"><Trash2 className="h-4 w-4" /></button>
      )}
    </div>
  );

  return (
    <>
      <ErrorBanner msg={error} />

      {/* Organization-level sections — only for users who can manage org config */}
      {canManageOrg && (<>
      {/* Source Code Connections summary — credentials live in Integration Settings */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <SectionTitle icon={Key} label="Source Code Connections" colorClass="text-purple-700"
            sub="GitHub / GitLab connections with their own tokens. Scan targets below reference a connection." />
          <button onClick={() => navigate("/settings/integrations")}
            className={`inline-flex items-center gap-2 px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn}`}>
            Manage Connections
          </button>
        </div>
        {connections.length > 0 ? (
          <div className="space-y-2">
            {connections.map(c => (
              <div key={c.id} className="flex items-center gap-2 py-2 px-3 bg-theme-primary rounded-md border border-border">
                <ProviderBadge provider={c.provider.toLowerCase()} />
                <span className="text-sm text-content-primary font-mono">{c.target}</span>
                {c.displayName && <span className="text-xs text-content-secondary">{c.displayName}</span>}
                {c.baseUrl && <span className="text-xs text-content-secondary">({c.baseUrl})</span>}
                <span className={`ml-auto text-xs ${c.tokenSet ? "text-green-600" : "text-red-500"}`}>
                  {c.tokenSet ? "token saved" : "no token"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-content-secondary">
            No connections configured yet. Add one under Settings → Integrations → Source Code Connections,
            then reference it in a scan target below.
          </p>
        )}
      </Card>

      {/* Organization Scan Targets */}
      <Card>
        <SectionTitle icon={Building2} label="Organization Scan Targets" colorClass="text-purple-700"
          sub="GitHub orgs and GitLab groups scanned at the organization level." />

        {orgTargets.length > 0 ? (
          <div className="space-y-2 mb-4">
            {orgTargets.map((entry) => {
              const globalIdx = scanTargets.indexOf(entry);
              return <TargetRow key={`org-${globalIdx}`} entry={entry} onDelete={() => handleDeleteTarget(globalIdx)} canDelete={canManageOrg} />;
            })}
          </div>
        ) : <p className="text-sm text-content-secondary mb-4">No organization-level targets configured.</p>}

        {canManageOrg && (connections.length > 0 ? (
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-content-secondary mb-1">Connection</label>
              <select value={newOrgSource} onChange={e => setNewOrgSource(e.target.value)}
                className={`w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-2 py-2 ${s.input}`}>
                {connections.map(c => (
                  <option key={c.id} value={String(c.id)}>
                    {c.provider} / {c.target}{c.displayName ? ` — ${c.displayName}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <button onClick={handleAddOrgTarget}
              disabled={addingOrgTarget || !newOrgSource}
              className={`inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn} disabled:opacity-50 disabled:cursor-not-allowed`}>
              {addingOrgTarget ? <Loader className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
            </button>
          </div>
        ) : (
          <p className="text-xs text-content-secondary">
            Add a Source Code Connection first (Settings → Integrations) — scan targets scan with a
            connection's token.
          </p>
        ))}
      </Card>
      </>)}

      {/* Deal Scan Targets */}
      {showDeals && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <SectionTitle icon={FileText} label="Deal Scan Targets" colorClass="text-purple-700"
              sub="Scan targets scoped to specific deals." />
            {canAddDeal && (
              <button onClick={() => setEnrollModal("deal")}
                className={`inline-flex items-center gap-2 px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn}`}>
                <Plus className="h-4 w-4" /> Add Deal
              </button>
            )}
          </div>
          {dealGroupList.length > 0 ? dealGroupList.map(g => (
            <div key={g.dealId} className="mb-3">
              <p className="text-sm font-medium text-content-primary mb-1">{dealNames[g.dealId] || `Deal #${g.dealId}`}</p>
              <div className="space-y-1 ml-4">
                {g.targets.map(t => <TargetRow key={t._idx} entry={t} onDelete={() => handleDeleteTarget(t._idx)} canDelete={canManageOrg || canManageDeal(g.dealId)} />)}
              </div>
            </div>
          )) : <p className="text-sm text-content-secondary">No deal-scoped targets.</p>}
        </Card>
      )}

      {/* BU Scan Targets */}
      {showBusinessUnits && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <SectionTitle icon={Building2} label="Business Unit Scan Targets" colorClass="text-purple-700"
              sub="Scan targets scoped to specific business units." />
            {canAddBU && (
              <button onClick={() => setEnrollModal("bu")}
                className={`inline-flex items-center gap-2 px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn}`}>
                <Plus className="h-4 w-4" /> Add Business Unit
              </button>
            )}
          </div>
          {buGroupList.length > 0 ? buGroupList.map(g => (
            <div key={g.businessUnitId} className="mb-3">
              <p className="text-sm font-medium text-content-primary mb-1">{buNames[g.businessUnitId] || `BU #${g.businessUnitId}`}</p>
              <div className="space-y-1 ml-4">
                {g.targets.map(t => <TargetRow key={t._idx} entry={t} onDelete={() => handleDeleteTarget(t._idx)} canDelete={canManageOrg || canManageBusinessUnit(g.businessUnitId)} />)}
              </div>
            </div>
          )) : <p className="text-sm text-content-secondary">No business unit-scoped targets.</p>}
        </Card>
      )}

      {/* Vendor Scan Targets */}
      {showTPRM && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <SectionTitle icon={Shield} label="Vendor Scan Targets" colorClass="text-purple-700"
              sub="Scan targets scoped to specific vendors." />
            {canAddVendor && (
              <button onClick={() => setEnrollModal("vendor")}
                className={`inline-flex items-center gap-2 px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn}`}>
                <Plus className="h-4 w-4" /> Add Vendor
              </button>
            )}
          </div>
          {vendorGroupList.length > 0 ? vendorGroupList.map(g => (
            <div key={g.vendorId} className="mb-3">
              <p className="text-sm font-medium text-content-primary mb-1">{vendorNames[g.vendorId] || `Vendor #${g.vendorId}`}</p>
              <div className="space-y-1 ml-4">
                {g.targets.map(t => <TargetRow key={t._idx} entry={t} onDelete={() => handleDeleteTarget(t._idx)} canDelete={canAddVendor} />)}
              </div>
            </div>
          )) : <p className="text-sm text-content-secondary">No vendor-scoped targets.</p>}
        </Card>
      )}

      {/* Domain Keywords + Scan Settings — org-level */}
      {canManageOrg && (<>
      {/* Domain Keywords */}
      <Card>
        <SectionTitle icon={Key} label="Domain Keywords" colorClass="text-purple-700"
          sub="Keywords searched across public GitHub code to find leaked secrets referencing your infrastructure." />
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-md">
          <Globe className="h-4 w-4 text-blue-500 shrink-0" />
          <p className="text-xs text-blue-700">Domain keyword scanning searches public GitHub code and runs independently of the scan targets above.</p>
        </div>
        {keywords.length > 0 && !connections.some(c => c.provider === "GitHub") && (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            <p className="text-xs text-amber-700">
              GitHub Code Search requires authentication — add a GitHub Source Code Connection
              (Settings → Integrations) or keyword scanning will be skipped.
            </p>
          </div>
        )}
        {keywords.length > 0 ? (
          <div className="space-y-2 mb-4">
            {keywords.map(kw => (
              <div key={kw} className="flex items-center justify-between py-2 px-3 bg-theme-primary rounded-md border border-border">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-content-secondary" />
                  <span className="text-sm text-content-primary">{kw}</span>
                </div>
                {canManageOrg && (
                  <button onClick={() => handleDeleteKw(kw)} disabled={deletingKw === kw}
                    className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1">
                    {deletingKw === kw ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-content-secondary mb-4">No keywords configured.</p>}
        {canManageOrg && (
          <div className="flex gap-3">
            <input type="text" value={newKeyword} onChange={e => setNewKeyword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAddKw()}
              placeholder="e.g. mycompany.com" disabled={addingKw}
              className={`flex-1 rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 ${s.input}`} />
            <button onClick={handleAddKw} disabled={addingKw || !newKeyword.trim()}
              className={`inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn} disabled:opacity-50 disabled:cursor-not-allowed`}>
              {addingKw ? <><Loader className="h-4 w-4 animate-spin" />Adding...</> : <><Plus className="h-4 w-4" />Add</>}
            </button>
          </div>
        )}
      </Card>

      {/* Scan Settings */}
      <Card>
        <FrequencyRow frequency={frequency} setFrequency={setFrequency} isEnabled={isEnabled}
          setIsEnabled={setIsEnabled} isAdmin={canManageOrg} inputClass={s.input} checkClass={s.check} enabledId="ss-enabled" />
      </Card>
      </>)}

      <ConfirmDeleteModal
        isOpen={deleteTargetIdx != null}
        onClose={() => setDeleteTargetIdx(null)}
        onConfirm={confirmDeleteTarget}
        isBusy={deletingTarget}
        requireTyping={false}
        title="Remove Scan Target"
        confirmLabel="Remove"
        message={deleteTargetIdx != null && scanTargets[deleteTargetIdx]
          ? `Are you sure you want to remove ${scanTargets[deleteTargetIdx].target}? This will delete all of its findings and associated risks.`
          : ""}
      />

      {/* Enrollment modal */}
      {enrollModal && (
        <SecretsScanEnrollEntityModal
          isOpen={!!enrollModal}
          entityType={enrollModal}
          connections={connections}
          existingEntityIds={
            enrollModal === "deal" ? dealGroupList.map(g => g.dealId) :
            enrollModal === "bu" ? buGroupList.map(g => g.businessUnitId) :
            vendorGroupList.map(g => g.vendorId)
          }
          onEnrolled={handleEntityEnrolled}
          onClose={() => setEnrollModal(null)}
        />
      )}
    </>
  );
}

// ─── Surface Scan Tab ─────────────────────────────────────────────────────────

export function SurfaceTab({ s }) {
  const apiCall = useApi();
  const { canManageOrganizationRisks, canManageDeal, canManageBusinessUnit, canCreateVendor, canManageAnyDeal, canManageAnyBusinessUnit } = useRBAC();
  const hasLicense = useHasLicense();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  // Per-scope gates: org-level config (org domains, scan settings, EASM) needs Enterprise
  // Risk Manager; deal/BU/vendor domains need management rights on that scope.
  const orgId = org?.organizationId;
  const canManageOrg = canManageOrganizationRisks(orgId);
  const canAddDeal = canManageAnyDeal(orgId);
  const canAddBU = canManageAnyBusinessUnit(orgId);
  const canAddVendor = canCreateVendor(orgId);
  // Remaining org-level controls below (scan settings, Microsoft/EASM, ransomware toggle)
  // all gate on Enterprise Risk Manager; alias keeps those references concise.
  const isAdmin = canManageOrg;
  const showDeals = hasLicense("Deals");
  const showBusinessUnits = hasLicense("BusinessUnits");
  const showTPRM = hasLicense("TPRM");

  const [config, setConfig]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [frequency, setFrequency]     = useState(24);
  const [isEnabled, setIsEnabled]     = useState(true);
  const [saving, setSaving]           = useState(false);
  const [domains, setDomains]             = useState([]);
  const [newDomain, setNewDomain]         = useState("");
  const [addingDomain, setAddingDomain]   = useState(false);
  const [deletingDomain, setDeletingDomain] = useState(null);
  const [deleteDomainPending, setDeleteDomainPending] = useState(null); // domain string pending confirm
  const [showDealModal, setShowDealModal] = useState(false);
  const [showBUModal, setShowBUModal]     = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [msEnabled, setMsEnabled]         = useState(false);
  const [easmEnabled, setEasmEnabled]     = useState(false);
  const [ransomwareMonitoringEnabled, setRansomwareMonitoringEnabled] = useState(true);
  const [dealNames, setDealNames]         = useState({});
  const [buNames, setBuNames]             = useState({});
  const [vendorNames, setVendorNames]     = useState({});

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const fetches = [
        apiCall("GET", "/esm/config").catch(() => null),
      ];
      if (showDeals) fetches.push(apiCall("GET", "/deals/all").catch(() => []));
      if (showBusinessUnits) fetches.push(apiCall("GET", "/businessUnits/all").catch(() => []));
      if (showTPRM) fetches.push(apiCall("GET", "/TPRMVendor/vendors?pageSize=100").catch(() => null));

      const results = await Promise.all(fetches);
      const [data] = results;
      let idx = 1;

      if (showDeals) {
        const deals = results[idx++] || [];
        const map = {};
        deals.forEach(d => { map[d.dealID] = d.dealName; });
        setDealNames(map);
      }
      if (showBusinessUnits) {
        const bus = results[idx++] || [];
        const map = {};
        bus.forEach(b => { map[b.businessUnitId] = b.businessUnitName; });
        setBuNames(map);
      }
      if (showTPRM) {
        const vendorRes = results[idx++];
        const vendors = vendorRes?.items || vendorRes || [];
        const map = {};
        vendors.forEach(v => { map[v.vendorId] = v.vendorName; });
        setVendorNames(map);
      }

      setConfig(data);
      if (data) {
        setFrequency(data.scanFrequencyHours || 24);
        setIsEnabled(data.isEnabled ?? true);
        setDomains(data.domains || []);
        setMsEnabled(data.msEnabled ?? false);
        setEasmEnabled(data.easmEnabled ?? false);
        setRansomwareMonitoringEnabled(data.ransomwareMonitoringEnabled ?? true);
      }
    } catch { setError("Failed to load configuration."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (updatedDomains, updatedKey) => {
    const payload = {
      domains: updatedDomains ?? domains,
      shodanApiKey: updatedKey ?? null,
      isEnabled,
      scanFrequencyHours: Number(frequency) || 24,
      msEnabled,
      easmEnabled,
      ransomwareMonitoringEnabled,
    };
    const saved = await apiCall("POST", "/esm/config", payload);
    setConfig(saved);
    setDomains(saved.domains || []);
    return saved;
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try { await save(domains, null); }
    catch { setError("Failed to save configuration."); }
    finally { setSaving(false); }
  };

  const handleAddDomain = async () => {
    let d = newDomain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!d) { setError("Please enter a domain."); return; }
    if (domains.some(e => e.domain === d)) { setError("Domain already added."); return; }
    setAddingDomain(true); setError(null);
    try { await save([...domains, { domain: d, vendorId: null }], null); setNewDomain(""); }
    catch { setError("Failed to add domain."); }
    finally { setAddingDomain(false); }
  };

  // Single-step "Are you sure?" confirm (no typed Confirm) — a removed domain can be re-added at any time.
  const handleDeleteDomain = (domain) => setDeleteDomainPending(domain);

  const confirmDeleteDomain = async () => {
    const domain = deleteDomainPending;
    if (!domain) return;
    setDeletingDomain(domain); setError(null);
    try { await save(domains.filter(e => e.domain !== domain), null); setDeleteDomainPending(null); }
    catch (err) { setError(err?.response?.data?.message || "Failed to remove domain."); setDeleteDomainPending(null); }
    finally { setDeletingDomain(null); }
  };

  // Group deal/BU/vendor domains from the domains array
  const dealGroups = {};
  const buGroups = {};
  const vendorGroups = {};
  domains.forEach(entry => {
    if (entry.dealId) {
      if (!dealGroups[entry.dealId]) dealGroups[entry.dealId] = { dealId: entry.dealId, domains: [] };
      dealGroups[entry.dealId].domains.push(entry);
    }
    if (entry.businessUnitId) {
      if (!buGroups[entry.businessUnitId]) buGroups[entry.businessUnitId] = { businessUnitId: entry.businessUnitId, domains: [] };
      buGroups[entry.businessUnitId].domains.push(entry);
    }
    if (entry.vendorId) {
      if (!vendorGroups[entry.vendorId]) vendorGroups[entry.vendorId] = { vendorId: entry.vendorId, domains: [] };
      vendorGroups[entry.vendorId].domains.push(entry);
    }
  });
  const dealGroupList = Object.values(dealGroups);
  const buGroupList = Object.values(buGroups);
  const vendorGroupList = Object.values(vendorGroups);

  const handleDealEnrolled = async (dealId, domain) => {
    setError(null);
    if (domains.some(e => e.domain === domain)) { setError("Domain already added."); return; }
    try {
      await save([...domains, { domain, vendorId: null, dealId, businessUnitId: null }], null);
    } catch { setError("Failed to add deal domain."); }
  };

  const handleBUEnrolled = async (businessUnitId, domain) => {
    setError(null);
    if (domains.some(e => e.domain === domain)) { setError("Domain already added."); return; }
    try {
      await save([...domains, { domain, vendorId: null, dealId: null, businessUnitId }], null);
    } catch { setError("Failed to add business unit domain."); }
  };

  const handleVendorEnrolled = async (vendorId, domain) => {
    setError(null);
    if (domains.some(e => e.domain === domain)) { setError("Domain already added."); return; }
    try {
      await save([...domains, { domain, vendorId, dealId: null, businessUnitId: null }], null);
    } catch { setError("Failed to add vendor domain."); }
  };

  // Entity (deal/BU/vendor) domains delete the same way — no confirmation.
  const handleDeleteEntityDomain = handleDeleteDomain;

  if (loading) return <div className="flex justify-center py-16"><Loader className={`h-7 w-7 animate-spin ${s.spin}`} /></div>;

  return (
    <>
      <ErrorBanner msg={error} />

      {/* Organization Domains — org-level, hidden from deal/BU-scoped users */}
      {canManageOrg && (
      <Card>
        <SectionTitle icon={Globe} label="Organization Domains" colorClass="text-purple-700"
          sub="Add the domains you want to monitor. Each domain is scanned for subdomains, TLS issues, DNS health, missing security headers, and open ports." />

        {easmEnabled && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <Cloud className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              Domains added here are automatically seeded into Microsoft Defender EASM.
              EASM findings (CVEs, open ports, cert issues) typically appear within 24 hours after Microsoft's crawl completes.
            </p>
          </div>
        )}

        {(() => {
          const orgDomains = domains.filter(e => !e.dealId && !e.businessUnitId && !e.vendorId);
          return orgDomains.length > 0 ? (
            <div className="space-y-2 mb-4">
              {orgDomains.map(entry => (
                <div key={entry.domain} className="flex items-center justify-between py-2 px-3 bg-theme-primary rounded-md border border-border">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-content-secondary" />
                    <span className="text-sm text-content-primary font-mono">{entry.domain}</span>
                  </div>
                  {canManageOrg && (
                    <button onClick={() => handleDeleteDomain(entry.domain)} disabled={deletingDomain === entry.domain}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1">
                      {deletingDomain === entry.domain ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-content-secondary mb-4">No domains configured.</p>;
        })()}

        {canManageOrg && (
          <div className="flex gap-3">
            <input type="text" value={newDomain} onChange={e => setNewDomain(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAddDomain()}
              placeholder="e.g. example.com" disabled={addingDomain}
              className={`flex-1 rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 ${s.input}`} />
            <button onClick={handleAddDomain} disabled={addingDomain || !newDomain.trim()}
              className={`inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn} disabled:opacity-50 disabled:cursor-not-allowed`}>
              {addingDomain ? <><Loader className="h-4 w-4 animate-spin" />Adding…</> : <><Plus className="h-4 w-4" />Add</>}
            </button>
          </div>
        )}
      </Card>
      )}

      {/* Deal Domains Section */}
      {showDeals && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <SectionTitle icon={FileText} label="Deal Domains" colorClass="text-purple-700"
              sub="Monitor additional domains scoped to specific deals. Risks from these domains will be linked to the deal." />
            {canAddDeal && (
              <button onClick={() => setShowDealModal(true)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white ${s.btn}`}>
                <Plus className="h-4 w-4" />
                Add Deal
              </button>
            )}
          </div>
          {dealGroupList.length > 0 ? (
            <div className="space-y-4">
              {dealGroupList.map((dealGroup) => (
                <div key={dealGroup.dealId} className="bg-theme-primary rounded-md border border-border p-3">
                  <h4 className="text-sm font-medium text-content-primary mb-2">{dealNames[dealGroup.dealId] || `Deal #${dealGroup.dealId}`}</h4>
                  <div className="space-y-2">
                    {dealGroup.domains.map((d) => (
                      <div key={d.domain} className="flex items-center justify-between py-1.5 px-2 bg-theme-secondary rounded border border-border">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-content-secondary" />
                          <span className="text-sm text-content-primary font-mono">{d.domain}</span>
                        </div>
                        {(canManageOrg || canManageDeal(dealGroup.dealId)) && (
                          <button onClick={() => handleDeleteEntityDomain(d.domain)} disabled={deletingDomain === d.domain}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1" title="Remove domain">
                            {deletingDomain === d.domain ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-content-secondary">No deal domains configured.</p>
          )}
        </Card>
      )}

      {/* Business Unit Domains Section */}
      {showBusinessUnits && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <SectionTitle icon={Building2} label="Business Unit Domains" colorClass="text-purple-700"
              sub="Monitor additional domains scoped to specific business units. Risks from these domains will be linked to the business unit." />
            {canAddBU && (
              <button onClick={() => setShowBUModal(true)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white ${s.btn}`}>
                <Plus className="h-4 w-4" />
                Add Business Unit
              </button>
            )}
          </div>
          {buGroupList.length > 0 ? (
            <div className="space-y-4">
              {buGroupList.map((buGroup) => (
                <div key={buGroup.businessUnitId} className="bg-theme-primary rounded-md border border-border p-3">
                  <h4 className="text-sm font-medium text-content-primary mb-2">{buNames[buGroup.businessUnitId] || `Business Unit #${buGroup.businessUnitId}`}</h4>
                  <div className="space-y-2">
                    {buGroup.domains.map((d) => (
                      <div key={d.domain} className="flex items-center justify-between py-1.5 px-2 bg-theme-secondary rounded border border-border">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-content-secondary" />
                          <span className="text-sm text-content-primary font-mono">{d.domain}</span>
                        </div>
                        {(canManageOrg || canManageBusinessUnit(buGroup.businessUnitId)) && (
                          <button onClick={() => handleDeleteEntityDomain(d.domain)} disabled={deletingDomain === d.domain}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1" title="Remove domain">
                            {deletingDomain === d.domain ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-content-secondary">No business unit domains configured.</p>
          )}
        </Card>
      )}

      {/* Vendor Domains Section */}
      {showTPRM && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <SectionTitle icon={Building2} label="Vendor Domains" colorClass="text-purple-700"
              sub="Monitor additional domains scoped to specific vendors. Risks from these domains will be linked to the vendor." />
            {canAddVendor && (
              <button onClick={() => setShowVendorModal(true)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white ${s.btn}`}>
                <Plus className="h-4 w-4" />
                Add Vendor
              </button>
            )}
          </div>
          {vendorGroupList.length > 0 ? (
            <div className="space-y-4">
              {vendorGroupList.map((vendorGroup) => (
                <div key={vendorGroup.vendorId} className="bg-theme-primary rounded-md border border-border p-3">
                  <h4 className="text-sm font-medium text-content-primary mb-2">{vendorNames[vendorGroup.vendorId] || `Vendor #${vendorGroup.vendorId}`}</h4>
                  <div className="space-y-2">
                    {vendorGroup.domains.map((d) => (
                      <div key={d.domain} className="flex items-center justify-between py-1.5 px-2 bg-theme-secondary rounded border border-border">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-content-secondary" />
                          <span className="text-sm text-content-primary font-mono">{d.domain}</span>
                        </div>
                        {canAddVendor && (
                          <button onClick={() => handleDeleteEntityDomain(d.domain)} disabled={deletingDomain === d.domain}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50 p-1" title="Remove domain">
                            {deletingDomain === d.domain ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-content-secondary">No vendor domains configured.</p>
          )}
        </Card>
      )}

      {/* Scan Settings + Microsoft/EASM + Ransomware — org-level */}
      {canManageOrg && (<>
      <Card>
        <SectionTitle icon={Globe} label="Scan Settings" colorClass="text-purple-700" />

        <FrequencyRow frequency={frequency} setFrequency={setFrequency} isEnabled={isEnabled}
          setIsEnabled={setIsEnabled} isAdmin={isAdmin} inputClass={s.input} checkClass={s.check} enabledId="esm-enabled" />

        {isAdmin && (
          <button onClick={handleSave} disabled={saving}
            className={`inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn} disabled:opacity-50 disabled:cursor-not-allowed`}>
            {saving ? <><Loader className="h-4 w-4 animate-spin" />Saving…</> : <><RefreshCw className="h-4 w-4" />Save Settings</>}
          </button>
        )}
      </Card>

      <Card>
        <SectionTitle icon={Shield} label="Ransomware Monitoring" colorClass="text-red-700"
          sub="Monitor ransomware leak sites for mentions of your monitored domains. When a match is found, a threat event and risk are automatically created." />

        <div className="flex items-center gap-2 mb-5">
          <input type="checkbox" id="ransomware-enabled"
            checked={ransomwareMonitoringEnabled}
            onChange={e => setRansomwareMonitoringEnabled(e.target.checked)}
            disabled={!isAdmin}
            className={`rounded border-gray-300 ${s.check}`} />
          <label htmlFor="ransomware-enabled"
            className="text-sm cursor-pointer text-content-primary">
            Enable ransomware leak site monitoring for this organization
          </label>
        </div>

        {isAdmin && (
          <button onClick={handleSave} disabled={saving}
            className={`inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${s.btn} disabled:opacity-50 disabled:cursor-not-allowed`}>
            {saving ? <><Loader className="h-4 w-4 animate-spin" />Saving…</> : <><RefreshCw className="h-4 w-4" />Save Settings</>}
          </button>
        )}
      </Card>

      </>)}

      <ConfirmDeleteModal
        isOpen={!!deleteDomainPending}
        onClose={() => setDeleteDomainPending(null)}
        onConfirm={confirmDeleteDomain}
        isBusy={!!deletingDomain}
        requireTyping={false}
        title="Remove Domain"
        confirmLabel="Remove"
        message={deleteDomainPending
          ? `Are you sure you want to remove ${deleteDomainPending}? This will delete all of its findings and associated risks.`
          : ""}
      />

      {/* Enrollment Modals */}
      <SurfaceScanEnrollDealModal
        isOpen={showDealModal}
        onClose={() => setShowDealModal(false)}
        onEnrolled={handleDealEnrolled}
        existingDealIds={dealGroupList.map(g => g.dealId)}
        existingDomains={domains.map(e => e.domain)}
      />
      <SurfaceScanEnrollBusinessUnitModal
        isOpen={showBUModal}
        onClose={() => setShowBUModal(false)}
        onEnrolled={handleBUEnrolled}
        existingBUIds={buGroupList.map(g => g.businessUnitId)}
        existingDomains={domains.map(e => e.domain)}
      />
      <SurfaceScanEnrollVendorModal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        onEnrolled={handleVendorEnrolled}
        existingVendorIds={vendorGroupList.map(g => g.vendorId)}
        existingDomains={domains.map(e => e.domain)}
      />
    </>
  );
}

// ─── Shared: Frequency Row ────────────────────────────────────────────────────

function FrequencyRow({ frequency, setFrequency, isEnabled, setIsEnabled, isAdmin, inputClass, checkClass, enabledId, extraOptions = [] }) {
  const baseOptions = [
    { value: 12, label: "Every 12 hours" },
    { value: 24, label: "Every 24 hours" },
    { value: 48, label: "Every 48 hours" },
    { value: 168, label: "Weekly" },
  ];
  const options = [...extraOptions, ...baseOptions].sort((a, b) => a.value - b.value);

  return (
    <div className="flex flex-wrap items-center gap-6 mb-5">
      <div>
        <label className="block text-sm font-medium text-content-secondary mb-1">Scan Frequency</label>
        <select value={frequency} onChange={e => setFrequency(Number(e.target.value))} disabled={!isAdmin}
          className={`rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 ${inputClass}`}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 mt-5">
        <input type="checkbox" id={enabledId} checked={isEnabled} onChange={e => setIsEnabled(e.target.checked)}
          disabled={!isAdmin} className={`rounded border-gray-300 ${checkClass}`} />
        <label htmlFor={enabledId} className="text-sm text-content-primary cursor-pointer">Enable automatic scanning</label>
      </div>
    </div>
  );
}
